import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Dropdown, Menu, Card, Empty } from 'antd';
import { SearchOutlined, LeftOutlined, RightOutlined, MoreOutlined } from '@ant-design/icons';
import { useQuery, useLazyQuery } from '@apollo/client';
import { Get_Account_Experts, Get_Expert_Video_By_Id, Get_Content_In_Other_Langauages } from '../../../services/userAPI';
import '../../Dashboard/Clan/knowYourRole/ContentLibrary/contentLibrary.css';
import { useLocation, useNavigate } from 'react-router-dom';
import Star from "../../../Images/starIcon.png";
import TranslateIcon from '@mui/icons-material/Translate';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import Arrow from "../../../Images/contentArrow.png";

// Separated Video Card component for better state isolation
const VideoCard = ({ video, expert, menuItems, activeDropdown, setActiveDropdown }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const titleRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

    const isTruncated = video.title?.length > 60;
    const duration = formatDuration(video.videoLengthMins || 0, video.videoLengthSecs || 0);

    const handleTitleHover = () => {
        if (video.title?.length > 60 && titleRef.current) {
            const rect = titleRef.current.getBoundingClientRect();
            setTooltipPosition({
                top: rect.bottom + 5,
                left: rect.left
            });
            setShowTooltip(true);
        }
    };

    const toggleExpansion = (e) => {
        e.stopPropagation();
        setIsExpanded((prev) => !prev);
    };

    return (
        <div className="video-card"
            style={{
                height: isExpanded ? 'auto' : '450px',
                transition: 'height 0.3s ease',
            }}
        >
            <div
                className="video-thumbnail"
                style={{
                    backgroundImage: `url(${video.vimeoThumbnailUrl || '/api/placeholder/320/180'})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div className="video-duration">{duration}</div>
                <div className="play-button">
                    <div className="play-icon"></div>
                </div>
            </div>
            {video.originalContent === true ? (
                <div className="original-label">
                    Original
                </div>
            ) : video.originalContent === false ? (
                <div className="original-placeholder"></div>
            ) : null}
            <div className="video-details">
                <h3
                    ref={titleRef}
                    className="video-title"
                    onMouseEnter={handleTitleHover}
                    onMouseLeave={() => setShowTooltip(false)}
                >
                    {isTruncated ? video.title.substring(0, 60) + "..." : video.title}
                </h3>
                {showTooltip && (
                    <div
                        className="tooltip"
                        style={{
                            position: 'fixed',
                            top: `${tooltipPosition.top}px`,
                            left: `${tooltipPosition.left}px`,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                            zIndex: 1000,
                            maxWidth: '300px',
                            wordWrap: 'break-word'
                        }}
                    >
                        {video.title}
                    </div>
                )}
                <div className="author-row">
                    <img
                        className="author-avatar"
                        src={Star}
                        alt={expert.name?.charAt(0) || '?'}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                        }}
                    />
                    <span className="author-name">{expert.name || 'Unknown Expert'}</span>
                    <Dropdown
                        overlay={menuItems(video.id)}
                        trigger={['click']}
                        open={activeDropdown === video.id}
                        onOpenChange={(visible) => {
                            setActiveDropdown(visible ? video.id : null);
                        }}
                    >
                        <Button
                            className="more-button"
                            icon={<MoreOutlined />}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </Dropdown>
                </div>
                <div className="video-tags">
                    <span className="language-tag" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <TranslateIcon style={{ width: '12px', height: '12px' }} />
                        {video.language || 'N/A'}
                    </span>
                    <span className="category-tag" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <CategoryOutlinedIcon style={{ width: '12px', height: '12px' }} />
                        {video.fourmatCategory || 'N/A'}
                    </span>
                </div>
                <p className="video-description">
                    {isExpanded ? video.description : video.description?.substring(0, 55) + "..."}
                </p>
                <div className="view-more-container">
                    <button
                        type="button"
                        className="view-more-button"
                        onClick={toggleExpansion}
                        onKeyDown={(e) => e.key === 'Enter' && toggleExpansion(e)}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? "Collapse video details" : "Expand video details"}
                    >
                        {isExpanded ? "View Less" : "View More"}
                        <span className={`view-more-arrow ${isExpanded ? "expanded" : ""}`}>
                            {isExpanded ? "▲" : "▼"}
                        </span>
                    </button>
                </div>
                {isExpanded && (
                    <div className="metadata-dropdown">
                        <div className="metadata-section">
                            <div className="metadata-header">Activity</div>
                            <div className="metadata-value">{video.activityName || 'N/A'}</div>
                        </div>
                        <div className="metadata-section">
                            <div className="metadata-header">Task</div>
                            <div className="metadata-value">{video.taskName || 'N/A'}</div>
                        </div>
                        <div className="metadata-section">
                            <div className="metadata-header">Sub-Task</div>
                            <div className="metadata-value">{video.subtaskName || 'N/A'}</div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Function to format video duration
function formatDuration(mins, secs) {
    const totalSeconds = (mins * 60) + secs;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

const AdminContentLibrary = () => {
    const location = useLocation();
    const accountId = location.state?.accountId;
    const roleId = location.state?.roleId;
    const accountName = location.state?.accountName;
    const name = location.state?.name;

    const navigate = useNavigate();

    const [expertPagination, setExpertPagination] = useState({});
    const [expertsPagination, setExpertsPagination] = useState({
        nextCursor: null,
        hasNextPage: false,
        loading: false,
    });
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [experts, setExperts] = useState([]);
    const [expertVideos, setExpertVideos] = useState({});
    const [selectedExpertId, setSelectedExpertId] = useState(null);

    const limit = 5;
    const sortByField = 'created_at';
    const sortOrder = 'desc';
    const [cursor, setCursor] = useState(null);

    const loaderRef = useRef(null);

    const [fetchExperts, { loading: expertsLoading, error: expertsError, data: expertsData }] = useLazyQuery(Get_Account_Experts, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const newExperts = data?.getAccountExperts?.data || [];
            setExperts(prev => cursor ? [...prev, ...newExperts] : newExperts); // Append if cursor exists
            setExpertsPagination({
                nextCursor: data?.getAccountExperts?.pageMeta?.nextPage || null,
                hasNextPage: data?.getAccountExperts?.pageMeta?.hasNextPage || false,
                loading: false,
            });
            if (newExperts.length > 0 && !selectedExpertId) {
                setSelectedExpertId(newExperts[0].id);
            }
        },
        onError: (error) => {
            console.error('Error fetching experts:', error);
            setExpertsPagination(prev => ({ ...prev, loading: false }));
        },
    });

    const [getExpertVideo] = useLazyQuery(Get_Expert_Video_By_Id, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            console.log('Get_Expert_Video_By_Id response:', data);
        },
        onError: (error) => {
            console.error('Get_Expert_Video_By_Id error:', error);
        }
    });

    const [getContentInOtherLanguages, { loading: otherLanguagesLoading, error: otherLanguagesError }] = useLazyQuery(Get_Content_In_Other_Langauages, {
        fetchPolicy: 'network-only'
    });

    // Initial fetch of experts
    useEffect(() => {
        fetchExperts({
            variables: {
                accountId,
                pagingCriteria: {
                    limit,
                    cursor: null,
                    sortByField,
                    sortOrder
                }
            }
        });
    }, [fetchExperts, accountId]);

    // Set up IntersectionObserver for lazy loading
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && expertsPagination.hasNextPage && !expertsPagination.loading) {
                    setExpertsPagination(prev => ({ ...prev, loading: true }));
                    setCursor(expertsPagination.nextCursor);
                    fetchExperts({
                        variables: {
                            accountId,
                            pagingCriteria: {
                                limit,
                                cursor: expertsPagination.nextCursor,
                                sortByField,
                                sortOrder
                            }
                        }
                    });
                }
            },
            { threshold: 0.1 }
        );

        if (loaderRef.current) {
            observer.observe(loaderRef.current);
        }

        return () => {
            if (loaderRef.current) {
                observer.unobserve(loaderRef.current);
            }
        };
    }, [expertsPagination, fetchExperts, accountId]);

    useEffect(() => {
        if (experts.length > 0) {
            experts.forEach((expert) => {
                const expertId = expert.id;
                if (!expertVideos[expertId]) {
                    loadVideosForExpert(expertId);
                }
            });
        }
    }, [experts]);

    const loadVideosForExpert = async (expertId, cursor = null, append = false) => {
        setExpertPagination(prev => ({
            ...prev,
            [expertId]: {
                ...(prev[expertId] || {}),
                loading: true
            }
        }));

        try {
            const res = await getExpertVideo({
                variables: {
                    userId: expertId,
                    accountId: accountId,
                    pagingCriteria: {
                        limit,
                        cursor,
                        sortByField,
                        sortOrder
                    }
                }
            });

            const newVideos = res.data.getUserAllContributions?.data || [];
            const pageMeta = res.data.getUserAllContributions?.pageMeta || {};

            setExpertVideos(prev => ({
                ...prev,
                [expertId]: append
                    ? [...(prev[expertId] || []), ...newVideos]
                    : newVideos
            }));

            setExpertPagination(prev => ({
                ...prev,
                [expertId]: {
                    nextCursor: pageMeta.nextPage,
                    prevCursor: pageMeta.previousPage,
                    hasNextPage: pageMeta.hasNextPage,
                    hasPreviousPage: pageMeta.hasPreviousPage,
                    loading: false
                }
            }));
        } catch (error) {
            console.error("Error loading videos:", error);
            setExpertPagination(prev => ({
                ...prev,
                [expertId]: {
                    ...(prev[expertId] || {}),
                    loading: false
                }
            }));
        }
    };

    const handleMenuClick = async (e, videoId) => {
        setActiveDropdown(null);
        try {
            let expert = null;
            let expertId = null;
            let videos = null;
            let clickedVideo = null;

            for (const [expId, expVideos] of Object.entries(expertVideos)) {
                const foundVideo = expVideos.find(v => String(v.id) === String(videoId));
                if (foundVideo) {
                    expertId = expId;
                    videos = expVideos;
                    expert = experts.find(ex => String(ex.id) === String(expId));
                    clickedVideo = foundVideo;
                    break;
                }
            }

            if (!videos || !expert || !clickedVideo) {
                console.error('Videos or expert not found');
                navigate('/admin-add-content-library', {
                    state: {
                        isEdit: true,
                        editData: {
                            videoId,
                            accountId,
                            roleId,
                            accountName,
                            name,
                            otherLanguagesContent: []
                        },
                        accountId,
                        roleId,
                        accountName,
                        name
                    }
                });
                return;
            }

            const stars = videos.map(video => {
                let quizQuestion = '';
                let quizOptionA = '';
                let quizOptionB = '';
                let correctAnswer = '';

                if (video.quizDetails && video.quizDetails.answer && video.quizDetails.answer.length >= 2) {
                    quizQuestion = video.quizDetails.question || '';

                    const sortedAnswers = [...video.quizDetails.answer].sort((a, b) => a.id - b.id);

                    quizOptionA = sortedAnswers[0]?.optionsAnswer || '';
                    quizOptionB = sortedAnswers[1]?.optionsAnswer || '';

                    const correctAnswerObj = video.quizDetails.answer.find(ans => ans.isanswer === true);
                    if (correctAnswerObj) {
                        if (correctAnswerObj.id === sortedAnswers[0].id) {
                            correctAnswer = 'A';
                        } else if (correctAnswerObj.id === sortedAnswers[1].id) {
                            correctAnswer = 'B';
                        } else {
                            correctAnswer = correctAnswerObj.optionsAnswer === quizOptionA ? 'A' : 'B';
                        }
                    }
                }

                return {
                    videoId: video.id,
                    accountId,
                    roleId,
                    userId: expertId,
                    name: expert.name,
                    employeeId: expert.employeeId || 'No ID',
                    youtubeCode: video.code || '',
                    videoTitle: video.title || '',
                    videoThumbnailCode: video.vimeoThumbnailUrl || '',
                    duration: formatDuration(video.videoLengthMins || 0, video.videoLengthSecs || 0),
                    videoLanguage: video.languageId || video.language || '',
                    originalContent: video.originalContent || false,
                    videoDescription: video.description || '',
                    keyPoints: video.keyPoints?.length ? video.keyPoints.map(kp => ({
                        id: kp.id,
                        keyPoint: kp.keyPoint,
                        contentId: kp.contentId
                    })) : [],
                    quizQuestion: quizQuestion,
                    quizOptionA: quizOptionA,
                    quizOptionB: quizOptionB,
                    correctAnswer: correctAnswer,
                    activityId: video.activityId || '',
                    activityName: video.activityName || '',
                    taskId: video.taskId || '',
                    taskName: video.taskName || '',
                    subTaskId: video.subtaskId || '',
                    subTaskName: video.subtaskName || '',
                    format: video.fourmatCategory || '',
                    commitmentNeeded: video.commitmentNeeded || false,
                    contentId: parseInt(video.id) || 0
                };
            });

            const sortedStars = [
                stars.find(s => String(s.videoId) === String(videoId)),
                ...stars.filter(s => String(s.videoId) !== String(videoId))
            ].filter(Boolean);

            let otherLanguagesContent = [];
            try {
                const { data: otherLanguagesData } = await getContentInOtherLanguages({
                    variables: { contentId: parseInt(videoId) }
                });
                const relatedContent = (otherLanguagesData?.getContentInOtherLangauages || []).map(item => ({
                    ...item,
                    videoId: item.id
                }));
                otherLanguagesContent = [...otherLanguagesContent, ...relatedContent];
            } catch (langError) {
                console.error(`Error fetching other languages for video ${videoId}:`, langError);
            }

            const videoIdsWithOtherContent = new Set(otherLanguagesContent.map(item => String(item.videoId)));
            if (!videoIdsWithOtherContent.has(String(videoId))) {
                otherLanguagesContent.push({
                    ...clickedVideo,
                    videoId: clickedVideo.id
                });
            }

            navigate('/admin-add-content-library', {
                state: {
                    isEdit: true,
                    editData: {
                        stars: sortedStars,
                        accountId,
                        roleId,
                        accountName,
                        name,
                        otherLanguagesContent
                    },
                    accountId,
                    roleId,
                    accountName,
                    name
                }
            });

        } catch (error) {
            console.error('Error in handleMenuClick:', error);
            navigate('/admin-add-content-library', {
                state: {
                    isEdit: true,
                    editData: {
                        videoId,
                        accountId,
                        roleId,
                        accountName,
                        name,
                        otherLanguagesContent: []
                    },
                    accountId,
                    roleId,
                    accountName,
                    name
                }
            });
        }
    };

    const menuItems = (videoId) => (
        <Menu onClick={(e) => handleMenuClick(e, videoId)}>
            <Menu.Item key="edit">Edit</Menu.Item>
            <Menu.Item key="delete">Delete</Menu.Item>
            <Menu.Item key="share">Share</Menu.Item>
        </Menu>
    );

    const filterVideosBySearch = (videos) => {
        if (!searchText.trim()) return videos;

        return videos.filter(video =>
            video.title?.toLowerCase().includes(searchText.toLowerCase()) ||
            video.description?.toLowerCase().includes(searchText.toLowerCase()) ||
            video.language?.toLowerCase().includes(searchText.toLowerCase()) ||
            video.fourmatCategory?.toLowerCase().includes(searchText.toLowerCase())
        );
    };

    const renderExpertContentSections = () => {
        return experts.map(expert => {
            const expertId = expert.id;
            const expertVideosData = expertVideos[expertId] || [];
            const filteredVideos = filterVideosBySearch(expertVideosData);
            const isLoading = expertPagination[expertId]?.loading;

            return (
                <div className="content-section" key={expertId}>
                    <div className="section-header">
                        <h2 className="section-title">{`${expert.name}'s Content`}</h2>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: "-1%" }}>
                            <span
                                className="moreVideosHeading"
                                onClick={() =>
                                    navigate('/more-videos', {
                                        state: {
                                            expertId: expert.id,
                                            accountId,
                                            accountName,
                                            name,
                                            roleId,
                                        },
                                    })
                                }
                            >
                                More Videos
                            </span>
                        </div>
                    </div>
                    <div className="video-grid">
                        {isLoading ? (
                            <div className="loading-container">
                                <div className="spinner"></div>
                                <p>Loading videos...</p>
                            </div>
                        ) : filteredVideos.length > 0 ? (
                            filteredVideos.map(video => (
                                <VideoCard
                                    key={video.id}
                                    video={video}
                                    expert={expert}
                                    menuItems={menuItems}
                                    activeDropdown={activeDropdown}
                                    setActiveDropdown={setActiveDropdown}
                                />
                            ))
                        ) : (
                            <Empty description="No videos found" />
                        )}
                    </div>
                </div>
            );
        });
    };

    return (

        <div className="content-library">
            <h1 className="library-title">Content Library</h1>
            <div className="search-add-container-content-library">
                <Input
                    placeholder="Search content..."
                    prefix={<SearchOutlined style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }} />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="search-input"
                />
                <button
                    className="add-content-button"
                    onClick={() => {
                        navigate('/admin-add-content-library', {
                            state: { roleId: roleId, accountId: accountId, accountName: accountName, name: name }
                        });
                    }}
                >
                    Add Content
                </button>
            </div>
            <Card>
                {expertsLoading && experts.length === 0 ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading experts...</p>
                    </div>
                ) : expertsError ? (
                    <div className="error-container">
                        <p>Error loading experts. Please try again later.</p>
                    </div>
                ) : experts.length > 0 ? (
                    renderExpertContentSections()
                ) : (
                    <Empty description="No experts found" />
                )}
                {/* Loader element for IntersectionObserver */}
                <div ref={loaderRef} style={{ height: '20px', textAlign: 'center' }}>
                    {expertsPagination.loading && (
                        <div className="loading-container">
                            <div className="spinner"></div>
                            <p>Loading more experts...</p>
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default AdminContentLibrary;